import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Search,
  ChevronLeft,
  MoreVertical,
  Filter,
  X,
  DollarSign,
  CheckCircle2,
  AlertTriangle,
  Rocket,
  Info,
  MessageSquare,
  Trash2,
  Check,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import moment from "moment/moment";
import Loader from "../../Loader/Loader";
import { useDispatch, useSelector } from "react-redux";
import { showToast } from "../../../Helper/Toast";
import { hitGetAllNotificationsProfessionalApi } from "../../../Redux/Professional/GetAllNotificationsProfessionalSlice";
import { hitUpdateNotificationSettingProfessionalApi } from "../../../Redux/Professional/UpdateNotificationSettingProfessionalSlice";
import Pagination from "../../Common/Pagination/Pagination";
import { useGlobalState } from "../../../Providers/GlobalStateProvider";

const FILTER_TYPES = [
  { key: "payments", label: "Payments", icon: DollarSign },
  { key: "approvals", label: "Approvals", icon: CheckCircle2 },
  { key: "warnings", label: "Warnings", icon: AlertTriangle },
  { key: "readyToGo", label: "Ready to go", icon: Rocket },
  { key: "information", label: "Information", icon: Info },
  { key: "messages", label: "Messages", icon: MessageSquare },
];

const TYPE_KEYWORDS = {
  payments: ["payment", "credit", "billing", "invoice", "deposit"],
  approvals: ["approv", "contract", "accepted"],
  warnings: ["warning", "login", "sign-in", "sign in", "detected", "security"],
  readyToGo: ["ready to go", "lead", "promotion", "job", "bid"],
  information: ["info", "verification", "verified", "update"],
  messages: ["message", "chat", "conversation"],
};

const formatNotifDate = (date) => {
  const m = moment(date);
  if (!m.isValid()) return "";
  if (m.isSame(moment(), "day")) return m.format("h:mm A");
  if (m.isSame(moment().subtract(1, "day"), "day")) return "Yesterday";
  return m.format("MM/DD/YYYY");
};

const isUnread = (notif) =>
  notif?.isRead === 0 || notif?.isRead === false || notif?.read === 0;

const getNotifType = (notif) => {
  const haystack = `${notif?.title || ""} ${notif?.message || ""}`.toLowerCase();
  for (const [key, words] of Object.entries(TYPE_KEYWORDS)) {
    if (words.some((w) => haystack.includes(w))) return key;
  }
  return "information";
};

const getNotifIcon = (type) => {
  switch (type) {
    case "warnings":
      return { Icon: AlertTriangle, className: "pro-notifications__type-icon--warning" };
    case "readyToGo":
      return { Icon: Rocket, className: "pro-notifications__type-icon--lead" };
    case "payments":
      return { Icon: DollarSign, className: "pro-notifications__type-icon--payment" };
    case "approvals":
      return { Icon: CheckCircle2, className: "pro-notifications__type-icon--approval" };
    case "messages":
      return { Icon: MessageSquare, className: "pro-notifications__type-icon--message" };
    default:
      return { Icon: Info, className: "pro-notifications__type-icon--info" };
  }
};

const ProfessionalNotifications = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { setGlobalLoading } = useGlobalState();
  const menuRef = useRef(null);

  const [currentPage, setCurrentPage] = useState(1);
  const dataPerPage = 10;

  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [notificationsData, setNotificationsData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [activeFilters, setActiveFilters] = useState(
    FILTER_TYPES.reduce((acc, t) => ({ ...acc, [t.key]: true }), {}),
  );
  const [draftFilters, setDraftFilters] = useState(activeFilters);

  const [professionalData] = useState(
    localStorage.getItem("professionalData")
      ? JSON.parse(localStorage.getItem("professionalData"))
      : null,
  );

  useEffect(() => {
    setGlobalLoading(true);
    const skip = currentPage - 1;
    dispatch(
      hitGetAllNotificationsProfessionalApi({ skip, limit: dataPerPage }),
    ).then(() => setGlobalLoading(false));
  }, [dispatch, currentPage, dataPerPage, setGlobalLoading]);

  useEffect(() => {
    if (searchTerm.length === 0 || searchTerm.length > 3) {
      setGlobalLoading(true);
      const skip = currentPage - 1;
      dispatch(
        hitGetAllNotificationsProfessionalApi({
          skip,
          limit: dataPerPage,
          search: searchTerm,
        }),
      ).then(() => setGlobalLoading(false));
    }
  }, [currentPage, dataPerPage, searchTerm, dispatch, setGlobalLoading]);

  const notificationsDataState = useSelector(
    (state) => state.getAllNotificationsProfessionalReducer.data,
  );

  useEffect(() => {
    if (notificationsDataState && notificationsDataState.status === true) {
      setNotificationsData(notificationsDataState.data || []);
      setTotalCount(notificationsDataState.count || 0);
      setUnreadCount(
        notificationsDataState.unreadNotificationsCount ??
          (notificationsDataState.data || []).filter(isUnread).length,
      );
      setLoading(false);
      setGlobalLoading(false);
    }
  }, [notificationsDataState, setGlobalLoading]);

  useEffect(() => {
    const handleOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setShowMenu(false);
      }
    };
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, []);

  const filteredNotifications = useMemo(() => {
    return notificationsData.filter((notif) => {
      const type = getNotifType(notif);
      return activeFilters[type] !== false;
    });
  }, [notificationsData, activeFilters]);

  const openFilterModal = () => {
    setDraftFilters({ ...activeFilters });
    setShowFilterModal(true);
  };

  const toggleDraftFilter = (key) => {
    setDraftFilters((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const applyFilters = () => {
    setActiveFilters({ ...draftFilters });
    setShowFilterModal(false);
  };

  const clearAllFilters = () => {
    const cleared = FILTER_TYPES.reduce((acc, t) => ({ ...acc, [t.key]: false }), {});
    setDraftFilters(cleared);
  };

  const handleClearAll = () => {
    showToast("Clear all is not available yet", "info");
    setShowMenu(false);
  };

  const handleMarkAllRead = () => {
    showToast("Mark all as read is not available yet", "info");
    setShowMenu(false);
  };

  const handleToggleNotifications = (e) => {
    const v = e.target.checked ? 1 : 0;
    localStorage.setItem(
      "professionalData",
      JSON.stringify({ ...professionalData, notificationOnOff: v }),
    );
    dispatch(hitUpdateNotificationSettingProfessionalApi({ notificationOnOff: v }))
      .then((response) => {
        if (response.payload?.status === true) {
          showToast("Notification settings updated", "success");
        }
      })
      .catch(() => showToast("Failed to update settings", "error"));
    setShowMenu(false);
  };

  if (loading) {
    setGlobalLoading(true);
    return (
      <div className="pro-notifications-page__loader">
        <Loader />
      </div>
    );
  }

  const totalPages = Math.ceil(totalCount / dataPerPage) || 1;
  const companyName = professionalData?.companyName || "Professional";

  return (
    <div className="pro-app-page pro-app-page--muted pro-notifications-page">
      <div className="custom_container pro-notifications-page__inner">
        <header className="pro-notifications-page__hero">
          <button
            type="button"
            className="pro-notifications-page__back"
            onClick={() => navigate(-1)}
            aria-label="Go back"
          >
            <ChevronLeft size={24} />
          </button>
          <div className="pro-notifications-page__hero-text">
            <h1>Professional Notifications</h1>
            <p>{companyName}</p>
          </div>
        </header>

        <section className="pro-notifications">
          <div className="pro-notifications__section-head">
            <div>
              <h2>Notifications</h2>
              <p>
                {unreadCount} unread • {totalCount} total
              </p>
            </div>
            <div className="pro-notifications__menu-wrap" ref={menuRef}>
              <button
                type="button"
                className="pro-notifications__menu-btn"
                onClick={() => setShowMenu((v) => !v)}
                aria-label="More options"
              >
                <MoreVertical size={20} />
              </button>
              {showMenu && (
                <div className="pro-notifications__menu">
                  <button type="button" onClick={handleMarkAllRead}>
                    Mark all as read
                  </button>
                  <button type="button" onClick={handleClearAll}>
                    Clear all
                  </button>
                  <label className="pro-notifications__menu-toggle">
                    <span>Notifications on</span>
                    <input
                      type="checkbox"
                      defaultChecked={professionalData?.notificationOnOff === 1}
                      onChange={handleToggleNotifications}
                    />
                    <span className="pro-notifications__toggle-slider" />
                  </label>
                </div>
              )}
            </div>
          </div>

          <div className="pro-notifications__search">
            <Search size={18} className="pro-notifications__search-icon" />
            <input
              type="text"
              placeholder="Search notifications..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <button
              type="button"
              className="pro-notifications__filter-btn"
              onClick={openFilterModal}
              aria-label="Filter notifications"
            >
              <Filter size={18} />
            </button>
          </div>

          <p className="pro-notifications__label">MOST RECENT</p>

          <div className="pro-notifications__list">
            {filteredNotifications.length === 0 ? (
              <div className="pro-notifications__empty">
                No notifications found
              </div>
            ) : (
              filteredNotifications.map((notif) => {
                const unread = isUnread(notif);
                const type = getNotifType(notif);
                const { Icon, className: iconClass } = getNotifIcon(type);
                const itemKey = notif._id || notif.id;

                return (
                  <article
                    key={itemKey}
                    className={`pro-notifications__item${unread ? " is-unread" : ""}`}
                  >
                    {unread && <span className="pro-notifications__accent" aria-hidden />}
                    <div className={`pro-notifications__type-icon ${iconClass}`}>
                      <Icon size={18} strokeWidth={2} />
                    </div>
                    <div className="pro-notifications__body">
                      <h3>{notif.title}</h3>
                      <p>{notif.message}</p>
                      <time>{formatNotifDate(notif.createdAt)}</time>
                    </div>
                    {unread && (
                      <span className="pro-notifications__dot" aria-label="Unread" />
                    )}
                  </article>
                );
              })
            )}
          </div>

          {totalPages > 1 && (
            <div className="pro-notifications__pagination">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={(page) => setCurrentPage(page)}
              />
            </div>
          )}
        </section>
      </div>

      {showFilterModal && (
        <div
          className="pro-notifications-filter-overlay"
          onClick={() => setShowFilterModal(false)}
          role="presentation"
        >
          <div
            className="pro-notifications-filter"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="notif-filter-title"
          >
            <header className="pro-notifications-filter__header">
              <button
                type="button"
                className="pro-notifications-filter__nav-btn"
                onClick={() => setShowFilterModal(false)}
                aria-label="Back"
              >
                <ChevronLeft size={22} />
              </button>
              <h2 id="notif-filter-title">Notification Settings</h2>
              <button
                type="button"
                className="pro-notifications-filter__nav-btn"
                onClick={() => setShowFilterModal(false)}
                aria-label="Close"
              >
                <X size={20} />
              </button>
            </header>

            <div className="pro-notifications-filter__body">
              <p className="pro-notifications-filter__section-label">Filter by Type</p>
              <ul className="pro-notifications-filter__list">
                {FILTER_TYPES.map(({ key, label, icon: TypeIcon }) => (
                  <li key={key}>
                    <label className="pro-notifications-filter__row">
                      <span className="pro-notifications-filter__row-left">
                        <TypeIcon size={18} strokeWidth={2} />
                        {label}
                      </span>
                      <input
                        type="checkbox"
                        checked={!!draftFilters[key]}
                        onChange={() => toggleDraftFilter(key)}
                      />
                      <span className="pro-notifications-filter__checkbox" />
                    </label>
                  </li>
                ))}
              </ul>
            </div>

            <footer className="pro-notifications-filter__footer">
              <button
                type="button"
                className="pro-notifications-filter__btn pro-notifications-filter__btn--outline"
                onClick={clearAllFilters}
              >
                <Trash2 size={16} />
                Clear All
              </button>
              <button
                type="button"
                className="pro-notifications-filter__btn pro-notifications-filter__btn--primary"
                onClick={applyFilters}
              >
                <Check size={16} />
                Apply
              </button>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfessionalNotifications;
